import { useState, useCallback } from 'react'
import { memo, NamedExoticComponent } from 'react'
import { Button } from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import { DrawerPrescList } from '@/apps/legacy/components/organisms/Drawer'
import { Yakureki } from '@/shared/types/yakurekiCore'
import { useToast } from '@/shared/contexts/ToastContext'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { type UseDrawerSelectionModeReturn } from '@/features/yakureki'

type Props = {
  selectedPatient?: Yakureki
  patientList: Yakureki[]
  selectYakureki: (patientId: string) => void
  BottomButtons: React.ReactNode[]
  isRecording?: boolean
  reloadPatientList: () => Promise<void>
  onRecordClick?: () => void
  // 患者選択モード (まとめて完了 / まとめて録音 を一元管理する hook の戻り値).
  // selection が必須である以上、selection を消費する callback / ヘルパも対称に必須.
  selection: UseDrawerSelectionModeReturn
  onStartGroupRecording: () => void
  onConfirmBatchDismiss: (ids: string[]) => Promise<void>
  isSelectableForGroup: (patient: Yakureki) => boolean
  // 薬剤師選択必須: 未選択時はリストをグレーアウト
  isPharmacistSelected?: boolean
}

export const DrawerToggleButton: NamedExoticComponent<Props> = memo(
  ({
    selectedPatient,
    patientList,
    selectYakureki,
    BottomButtons,
    isRecording,
    reloadPatientList,
    onRecordClick,
    selection,
    onStartGroupRecording,
    onConfirmBatchDismiss,
    isSelectableForGroup,
    isPharmacistSelected,
  }) => {
    const [isOpen, setIsOpen] = useState(false)
    const { showToast } = useToast()
    const isUseNsips = localStorageWrapper.getItem('IsUseNsips') || false

    const open = useCallback(() => {
      // NSIPSモードが有効で録音中の場合は警告を表示
      if (isUseNsips && isRecording) {
        showToast('録音中に患者の変更はできません', 'warning', 3000)
        return
      }
      setIsOpen(true)
    }, [isUseNsips, isRecording, showToast])

    const close = useCallback(() => setIsOpen(false), [])

    // 録音中にNSIPSモードが有効な場合に患者切り替えをブロックする関数
    const handleSelectYakureki = useCallback(
      (yakurekiId: string) => {
        if (isUseNsips && isRecording) {
          showToast('録音中に患者の変更はできません', 'warning', 3000)
          close()
          return
        }
        selectYakureki(yakurekiId)
        close()
      },
      [isUseNsips, isRecording, selectYakureki, close, showToast]
    )

    return (
      <>
        <Button
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
            }
          }}
          onClick={open}
          startIcon={<PeopleIcon />}
          variant="outlined"
          sx={{
            color: colorTokens.text.secondary,
            borderColor: colorTokens.text.secondary,
            borderWidth: '2px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderColor: colorTokens.text.secondary,
              borderWidth: '2px',
            },
            minWidth: '120px',
          }}
        >
          患者メニュー
        </Button>
        <DrawerPrescList
          isOpen={isOpen}
          onClose={close}
          yakurekiList={patientList}
          selectedYakureki={selectedPatient}
          selectYakureki={handleSelectYakureki}
          BottomButtons={BottomButtons}
          resetYakurekiList={reloadPatientList}
          onRecordClick={onRecordClick}
          selection={selection}
          onStartGroupRecording={onStartGroupRecording}
          onConfirmBatchDismiss={onConfirmBatchDismiss}
          isSelectableForGroup={isSelectableForGroup}
          isPharmacistSelected={isPharmacistSelected}
        />
      </>
    )
  }
)

DrawerToggleButton.displayName = 'DrawerToggleButton'
